
import {
  defineComponent,
  ref,
  computed,
  PropType,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Tooltip from 'primevue/tooltip';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { TemplateStructureEnum } from '@/types';
import Filter from '@/components/table/Filter.vue';
import { isDateInRange } from '@/helpers/Date';

// Extended interface to include name_marketing_workflow and date_creation from GraphQL response
interface ExtendedTemplatesCustom extends TemplatesCustom {
  name_marketing_workflow?: string;
  date_creation?: string;
  date_modification?: string;
}

export default defineComponent({
  name: 'TemplateDataTable',

  components: {
    DataTable,
    Column,
    Button,
    Dropdown,
    Filter,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    templates: {
      type: Array as PropType<ExtendedTemplatesCustom[]>,
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    elementPartTypeOrSubtype: {
      type: String,
      required: true,
    },

    selectedTemplates: {
      type: Set as PropType<Set<number>>,
      required: true,
    },

    selectedSectionsByTemplate: {
      type: Map as PropType<Map<number, string[]>>,
      required: true,
    },
  },

  emits: ['preview-template', 'selection-changed', 'open-section-modal'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const pageSize = ref(10);
    const currentPage = ref(0);
    const paginatorTemplate = ref('FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport');
    const localSelectedTemplates = ref<number[]>(Array.from(props.selectedTemplates));
    const lastEmittedSelection = ref<number[]>(Array.from(props.selectedTemplates));
    const previewLoading = ref<Record<number, boolean>>({});

    // Selection system
    const groupSelection = ref<string | null>(null);
    const selection = [
      {
        code: 'unselect',
        label: t('bulkActions.selection.unselect'),
      },
      {
        code: 'visible',
        label: t('bulkActions.selection.visible'),
      },
      {
        code: 'all',
        label: t('bulkActions.selection.all'),
      },
    ];

    // Filtered templates storage (reactive)
    const filteredTemplates = ref<ExtendedTemplatesCustom[]>([]);

    // Check if element part type is a section
    const isElementPartTypeSection = computed(() => props.elementPartTypeOrSubtype === TemplateStructureEnum.SECTION);

    // Watch for changes in selectedTemplates prop to sync with local state
    watch(() => props.selectedTemplates, (newSelection) => {
      localSelectedTemplates.value = Array.from(newSelection);
      lastEmittedSelection.value = Array.from(newSelection);
    }, { deep: true });

    const onTemplateSelectionChanged = () => {
      // Avoid emitting if selection hasn't actually changed
      const currentSelection = [...localSelectedTemplates.value].sort();
      const lastSelection = [...lastEmittedSelection.value].sort();

      if (JSON.stringify(currentSelection) === JSON.stringify(lastSelection)) {
        return;
      }

      lastEmittedSelection.value = [...localSelectedTemplates.value];

      emit('selection-changed', {
        selectedIds: localSelectedTemplates.value,
        selectedSectionsByTemplate: Object.fromEntries(props.selectedSectionsByTemplate),
      });
    };

    // Selection functions inspired by SpmTable.vue
    const selectAllTemplates = async () => {
      if (groupSelection.value === 'unselect') {
        localSelectedTemplates.value = [];
      } else if (groupSelection.value === 'all') {
        // Select all filtered templates
        localSelectedTemplates.value = filteredTemplates.value.map((item) => item.id_template);
      } else if (groupSelection.value === 'visible') {
        // Select only templates visible on current page
        // Use nextTick to ensure currentPage is updated
        await nextTick();
        const startIndex = currentPage.value * pageSize.value;
        const endIndex = Math.min(startIndex + pageSize.value, filteredTemplates.value.length);
        const visibleTemplates = filteredTemplates.value.slice(startIndex, endIndex);
        const newSelections = visibleTemplates.map((item) => item.id_template);
        const existingSelections = localSelectedTemplates.value.filter((id) => !newSelections.includes(id));
        localSelectedTemplates.value = [...existingSelections, ...newSelections];
      }

      // Emit the change
      nextTick(() => {
        onTemplateSelectionChanged();
      });
    };

    // Clean selection to keep only templates present in filtered results
    const cleanSelection = () => {
      const filteredTemplateIds = filteredTemplates.value.map((item) => item.id_template);
      const cleanedSelection = localSelectedTemplates.value.filter((id) => filteredTemplateIds.includes(id));

      // Only update if the selection actually changed
      if (cleanedSelection.length !== localSelectedTemplates.value.length) {
        localSelectedTemplates.value = cleanedSelection;
        groupSelection.value = null;
        nextTick(() => {
          onTemplateSelectionChanged();
        });
      }
    };

    // Find template by ID
    const findTemplateById = (templateId: number): ExtendedTemplatesCustom | undefined => props.templates.find((item) => item.id_template === templateId);

    // Get selected sections for a template
    const getSelectedSectionsForTemplate = (templateId: number): string[] => props.selectedSectionsByTemplate.get(templateId) || [];

    const onPageChange = async (event: any) => {
      currentPage.value = event.page;
    };

    const handleTemplateCheckboxClick = (templateId: number) => {
      // If element part type is a section, open modal
      if (isElementPartTypeSection.value) {
        // Reset the checkbox state since we don't want it to change
        nextTick(() => {
          const checkbox = document.getElementById(`checkbox-${templateId}`) as HTMLInputElement;
          if (checkbox) {
            checkbox.checked = localSelectedTemplates.value.includes(templateId);
          }
        });

        const template = findTemplateById(templateId);
        if (template) {
          emit('open-section-modal', template);
        }
        return;
      }

      // For non-section elements, update selection based on checkbox state
      const checkbox = document.getElementById(`checkbox-${templateId}`) as HTMLInputElement;
      if (checkbox) {
        if (checkbox.checked) {
          // Add to selection
          if (!localSelectedTemplates.value.includes(templateId)) {
            localSelectedTemplates.value = [...localSelectedTemplates.value, templateId];
          }
        } else {
          // Remove from selection
          localSelectedTemplates.value = localSelectedTemplates.value.filter((id) => id !== templateId);
        }
      }

      // Emit the change after nextTick
      nextTick(() => {
        onTemplateSelectionChanged();
      });
    };

    // Configuration des colonnes avec filtres
    const templateColumns = [
      {
        field: 'label',
        header: t('templateBuilder.saveSyncElementModal.templateListView.columns.name'),
        type: 'text',
        filterable: true,
        sortable: true,
        editable: false,
        style: 'width: 60%',
        matchModes: [
          { label: t('filters.contains'), value: 'contains' },
          { label: t('filters.startsWith'), value: 'startsWith' },
          { label: t('filters.endsWith'), value: 'endsWith' },
          { label: t('filters.equals'), value: 'equals' },
        ],
      },
      {
        field: 'date_modification',
        header: t('campaigns.bulk.list.columns.last_update'),
        type: 'date',
        filterable: true,
        sortable: true,
        editable: false,
        style: 'width: 40%',
        filterSettings: { type: 'date' },
        matchModes: [
          { label: t('filters.interval'), value: 'interval' },
        ],
      },
    ];

    // Active filters for visual indication
    const activeFilters = ref<Set<string>>(new Set());

    // Initialize filter params for PrimeVue
    const filterParams = ref<Record<string, any>>(templateColumns
      .filter((col) => col.filterable)
      .reduce<object>((acc, col) => {
        let matchMode = 'contains';
        if (col.filterSettings?.type === 'date') {
          matchMode = 'interval';
        }
        return ({
          ...acc,
          [col.field]: {
            operator: null,
            constraints: [{ value: '', matchMode }],
          },
        });
      }, {}));

    // Filter function like SpmTable.vue filterClientSideTableData
    const filterClientSideTemplateData = () => {
      filteredTemplates.value = props.templates.filter((template) => Object.entries(filterParams.value).every(([column, filter]) => {
        const { matchMode: filterMatchMode, value: filterConstraintValue } = filter.constraints[0];
        const columnValue = String(template[column as keyof typeof template] || '');

        if (
          filterConstraintValue === ''
            || (Array.isArray(filterConstraintValue) && filterConstraintValue.length === 0)
        ) return true;

        const filterValueStr = String(filterConstraintValue).toLowerCase();
        const columnValueStr = columnValue.toLowerCase();

        if (filterMatchMode === 'startsWith') {
          return columnValueStr.startsWith(filterValueStr);
        }

        if (filterMatchMode === 'endsWith') {
          return columnValueStr.endsWith(filterValueStr);
        }

        if (filterMatchMode === 'equals') {
          return columnValueStr === filterValueStr;
        }

        if (filterMatchMode === 'interval' && column === 'date_modification') {
          // Handle date filtering for interval mode
          return isDateInRange(columnValue, filterConstraintValue);
        }

        // Default to 'contains'
        return columnValueStr.includes(filterValueStr);
      }));
    };

    // Get filter status for visual indication
    const getFilterStatus = (field: string) => (activeFilters.value.has(field) ? 'column-filter-active' : '');

    // Clear filter
    const clearFilter = (filterModel: any, filterCallback: Function, field: string) => {
      // Clear the filterModel constraints directly like in SpmTable.vue
      if (filterModel.constraints && filterModel.constraints[0]) {
        // eslint-disable-next-line no-param-reassign
        filterModel.constraints[0].value = '';
        // eslint-disable-next-line no-param-reassign
        filterModel.constraints[0].matchMode = field === 'date_modification' ? 'interval' : 'contains';
      }

      // Force reactivity by creating new object
      const newFilterParams = { ...filterParams.value };
      if (newFilterParams[field]) {
        newFilterParams[field] = {
          ...newFilterParams[field],
          constraints: [{
            value: '',
            matchMode: field === 'date_modification' ? 'interval' : 'contains',
          }],
        };
      }
      filterParams.value = newFilterParams;

      // Apply client-side filtering like SpmTable.vue
      filterClientSideTemplateData();

      // Clean selection to keep only templates present in filtered results
      cleanSelection();

      activeFilters.value.delete(field);
      currentPage.value = 0;
      filterCallback();
    };

    // Apply filter
    const applyFilter = (filterModel: any, filterCallback: Function, field: string) => {
      // Read from filterModel constraints like in SpmTable.vue
      const filterConstraintValue = filterModel.constraints[0].value;
      const filterMatchMode = filterModel.constraints[0].matchMode;

      // Force reactivity by creating new object
      const newFilterParams = { ...filterParams.value };
      if (newFilterParams[field]) {
        newFilterParams[field] = {
          ...newFilterParams[field],
          constraints: [{
            value: filterConstraintValue,
            matchMode: filterMatchMode,
          }],
        };
      }
      filterParams.value = newFilterParams;

      // Apply client-side filtering like SpmTable.vue
      filterClientSideTemplateData();

      // Clean selection to keep only templates present in filtered results
      cleanSelection();

      // Manage visual indication
      if (filterConstraintValue && (typeof filterConstraintValue === 'object' || String(filterConstraintValue).trim() !== '')) {
        activeFilters.value.add(field);
      } else {
        activeFilters.value.delete(field);
      }

      currentPage.value = 0;
      filterCallback();
    };

    // Handle preview with loading state like in MyTemplates.vue
    const handlePreviewWithLoading = (templateData: ExtendedTemplatesCustom) => {
      previewLoading.value[templateData.id_template] = true;

      emit('preview-template', {
        id: templateData.id_template,
        templateType: templateData.type,
        templateLabel: templateData.label,
      }, () => {
        previewLoading.value[templateData.id_template] = false;
      });
    };

    // Initialize filtered templates with all data when props change
    watch(() => props.templates, (newTemplates) => {
      filteredTemplates.value = [...newTemplates];
    }, { immediate: true });

    return {
      t,
      filteredTemplates,
      previewLoading,
      isElementPartTypeSection,
      filterParams,
      templateColumns,
      localSelectedTemplates,
      pageSize,
      paginatorTemplate,
      groupSelection,
      selection,
      selectAllTemplates,
      handleTemplateCheckboxClick,
      handlePreviewWithLoading,
      getSelectedSectionsForTemplate,
      clearFilter,
      applyFilter,
      getFilterStatus,
      onPageChange,
    };
  },
});
